import React, { useEffect, useMemo, useState } from 'react';
import { Building2, ShoppingCart, Store, Package, ArrowRight, BadgeCheck } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { ecommerceService } from '../../services/ecommerceService';

const normalizeFontFamily = (font) => {
  if (!font) return 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
  return font.includes(',') ? font : `"${font}", sans-serif`;
};

const resolveStoreRefId = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  return ref.storeId || ref.id || null;
};

const normalizeMallStore = (store, fallbackId = null) => {
  const id = store?.id || store?.storeId || fallbackId;
  const items = (store?.items || store?.products || store?.services || []).map((item, index) => ({
    ...item,
    id: item.id || `${id}-item-${index}`,
    storeId: item.storeId || id,
    price: Number(item.salePrice || item.price || 0),
    imageUrl: item.images?.[0] || item.imageUrl || '',
  }));

  return {
    id,
    storeName: store?.storeName || store?.name || 'Store',
    description: store?.description || '',
    logoUrl: store?.logoUrl || '',
    coverImage: store?.coverImage || store?.headerBackgroundUrl || '',
    storeType: store?.storeType || 'general',
    items
  };
};

export const DigitalMallTemplate = ({ bookData, storeSettings = {}, styling = {}, isPreview = false }) => {
  const { cart, addToCart, getCartTotal, getCartItemCount } = useCart();
  const [stores, setStores] = useState([]);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [loading, setLoading] = useState(true);

  const mallSettings = bookData?.mallSettings || storeSettings?.mallSettings || {};
  const centralCheckoutEnabled = mallSettings.centralizedCheckout !== false;
  const mallName = mallSettings.mallName || bookData?.botName || bookData?.title || 'Digital Mall';
  const mallTagline = mallSettings.tagline || 'Browse multiple stores and checkout in one place';
  const fontFamily = normalizeFontFamily(
    styling?.fontFamily || bookData?.design?.fontFamily || storeSettings?.fontFamily
  );
  const hasCentralCheckoutRoute = Boolean(
    mallSettings.checkoutUrl || mallSettings.whatsapp || mallSettings.email
  );

  useEffect(() => {
    const loadMallStores = async () => {
      setLoading(true);
      try {
        const inlineStores = Array.isArray(mallSettings.stores) ? mallSettings.stores : [];
        const inlineNormalized = inlineStores
          .filter((store) => store && (store.id || store.storeId || store.storeName))
          .map((store, index) => normalizeMallStore(store, `inline-store-${index}`));

        if (isPreview) {
          setStores(inlineNormalized);
          if (inlineNormalized.length > 0) {
            setActiveStoreId((prev) => prev || inlineNormalized[0].id);
          }
          return;
        }

        const refsToLoad = inlineStores.filter((store) => {
          const storeId = resolveStoreRefId(store);
          const hasInlineItems = (store?.items || store?.products || store?.services || []).length > 0;
          return storeId && !hasInlineItems;
        });

        const fetchedStores = await Promise.all(
          refsToLoad.map(async (storeRef) => {
            const storeId = resolveStoreRefId(storeRef);
            if (!storeId) return null;
            try {
              const [storeData, products] = await Promise.all([
                ecommerceService.getStore(storeId),
                ecommerceService.getProducts(storeId)
              ]);
              return normalizeMallStore({
                ...storeData,
                items: products
              });
            } catch (error) {
              console.error('Error loading mall store:', storeId, error);
              return null;
            }
          })
        );

        const merged = [...inlineNormalized];
        fetchedStores.filter(Boolean).forEach((store) => {
          if (!merged.some((existing) => existing.id === store.id)) {
            merged.push(store);
          }
        });

        setStores(merged);
        if (merged.length > 0) {
          setActiveStoreId((prev) => prev || merged[0].id);
        }
      } finally {
        setLoading(false);
      }
    };

    loadMallStores();
  }, [mallSettings.stores, isPreview]);

  const activeStore = useMemo(
    () => stores.find((store) => store.id === activeStoreId) || stores[0] || null,
    [stores, activeStoreId]
  );

  const cartGroupedByStore = useMemo(() => {
    return cart.reduce((acc, item) => {
      const key = item.storeId || 'unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [cart]);

  const handleCentralCheckout = () => {
    const cartSummary = cart
      .map((item) => `- ${item.quantity}x ${item.name} (R${Number(item.price || 0).toFixed(2)})`)
      .join('\n');
    const message = `Hi, I would like to complete checkout for these items:\n${cartSummary}\nTotal: R${getCartTotal().toFixed(2)}`;

    if (mallSettings.checkoutUrl) {
      window.location.href = mallSettings.checkoutUrl;
      return;
    }

    if (mallSettings.whatsapp) {
      window.open(`https://wa.me/${mallSettings.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
      return;
    }

    if (mallSettings.email) {
      window.location.href = `mailto:${mallSettings.email}?subject=${encodeURIComponent('Centralized Mall Checkout')}&body=${encodeURIComponent(message)}`;
    }
  };

  return (
    <div className={`${isPreview ? 'min-h-0' : 'min-h-screen'} bg-slate-50`} style={{ fontFamily }}>
      <div className={`mx-auto max-w-7xl px-4 ${isPreview ? 'py-4' : 'py-8'} sm:px-6 lg:px-8`}>
        <section className="mb-8 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 p-6 text-white sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-200">Digital Mall</p>
              <h1 className="mt-2 text-3xl font-bold sm:text-4xl">{mallName}</h1>
              <p className="mt-2 text-slate-200">{mallTagline}</p>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3">
              <ShoppingCart className="h-5 w-5" />
              <div>
                <p className="text-sm text-slate-200">{getCartItemCount()} item(s)</p>
                <p className="text-lg font-semibold">R{getCartTotal().toFixed(2)}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900">Storefronts</h2>
            {centralCheckoutEnabled && (
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
                <BadgeCheck className="h-4 w-4" />
                Centralized Checkout Enabled
              </div>
            )}
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              Loading storefronts...
            </div>
          ) : stores.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <Building2 className="mx-auto mb-3 h-8 w-8 text-slate-400" />
              <p className="text-slate-600">No storefronts connected yet.</p>
              <p className="mt-1 text-sm text-slate-500">Add stores to `mallSettings.stores` to populate this mall.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {stores.map((store) => (
                <button
                  key={store.id}
                  onClick={() => setActiveStoreId(store.id)}
                  className={`overflow-hidden rounded-2xl border bg-white text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
                    activeStore?.id === store.id ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-slate-200'
                  }`}
                >
                  <div className="h-28 w-full bg-slate-100">
                    {store.coverImage ? (
                      <img src={store.coverImage} alt={store.storeName} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400">
                        <Store className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-lg font-semibold text-slate-900">{store.storeName}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">{store.description || 'Browse store offerings'}</p>
                    <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                      {store.items.length} listing(s)
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {activeStore && (
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-slate-900">{activeStore.storeName}</h3>
                <p className="text-sm text-slate-500">{activeStore.items.length} listing(s)</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {activeStore.items.map((item) => (
                  <article key={item.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="aspect-[4/3] bg-slate-100">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-400">
                          <Package className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="line-clamp-2 font-semibold text-slate-900">{item.name}</h4>
                      {item.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{item.description}</p>}
                      <div className="mt-4 flex items-center justify-between">
                        <p className="text-xl font-bold text-slate-900">R{Number(item.price || 0).toFixed(2)}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(
                              {
                                ...item,
                                storeId: activeStore.id,
                                images: item.images || (item.imageUrl ? [item.imageUrl] : [])
                              },
                              1
                            );
                          }}
                          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
                        >
                          <ShoppingCart className="h-4 w-4" />
                          Add
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h4 className="text-lg font-semibold text-slate-900">Central Cart</h4>
                <p className="mt-1 text-sm text-slate-500">Items from all storefronts</p>

                <div className="mt-4 space-y-3">
                  {Object.keys(cartGroupedByStore).length === 0 ? (
                    <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">Your cart is empty.</p>
                  ) : (
                    Object.entries(cartGroupedByStore).map(([storeId, items]) => (
                      <div key={storeId} className="rounded-xl border border-slate-200 p-3">
                        <p className="text-sm font-semibold text-slate-800">
                          {stores.find((store) => store.id === storeId)?.storeName || 'Store'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">{items.length} line item(s)</p>
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-5 border-t border-slate-200 pt-4">
                  <div className="mb-3 flex items-center justify-between text-sm text-slate-600">
                    <span>Total items</span>
                    <span>{getCartItemCount()}</span>
                  </div>
                  <div className="mb-4 flex items-center justify-between text-base font-semibold text-slate-900">
                    <span>Total</span>
                    <span>R{getCartTotal().toFixed(2)}</span>
                  </div>
                  <button
                    onClick={handleCentralCheckout}
                    disabled={!centralCheckoutEnabled || getCartItemCount() === 0 || !hasCentralCheckoutRoute}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${
                      centralCheckoutEnabled && getCartItemCount() > 0 && hasCentralCheckoutRoute
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'cursor-not-allowed bg-slate-200 text-slate-500'
                    }`}
                  >
                    Proceed to Centralized Checkout
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  {!hasCentralCheckoutRoute && (
                    <p className="mt-2 text-xs text-slate-500">
                      Add `checkoutUrl`, `whatsapp`, or `email` in `mallSettings` to activate checkout.
                    </p>
                  )}
                </div>
              </div>
            </aside>
          </section>
        )}
      </div>
    </div>
  );
};
